/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { BaseFeatureMock } from '../mockServer/featureMock';
import {
    CashRegisterModelsListDto,
    CashRegisterModelDetailsDto,
    FactoryNumbersListDto,
    CheckSumsListDto,
    AttestationResponseDto,
    AttestationResponseStatus,
    VersionsListDto,
    ModelDocumentsListDto,
    PresignedFileInfo,
    SoftwareVersionItemDto,
} from './cashRegisterModelsClient.typings';
import {
    checkSumDtoFactory,
    cashRegisterModelsListItemFactory,
    cashRegisterModelDetailsDtoFactory,
    cashRegistersFactoryNumbersListDto,
    versionDtoFactory,
    modelDocumentDtoFactory,
    presignedFileInfoDtoFactory,
    softwareVersionDataFactory,
} from './cashRegisterModelsFactories';

const CASH_REGISTER_MODELS_URL: RegExp = /\/cashRegisterModels$/;
const CASH_REGISTER_MODEL_URL: RegExp = /\/cashRegisterModels\/(\d+)$/;
//---for CashRegister (not model) ---//
const CASH_REGISTERS_FACTORY_NUMBERS_URL: RegExp = /\/cashRegisterModels\/(\d+)\/cashRegisters$/;
//-------------------------
const MODEL_VERSIONS_URL: RegExp = /\/cashRegisterModels\/(\d+)\/versions$/;
const VERSION_CHECK_SUMS_URL: RegExp = /\/cashRegisterModels\/(\d+)\/versions\/(\d+)\/checksums$/;
const START_ATTESTATION_URL: RegExp = /\/cashRegisterModels\/(\d+)\/startAttestation$/;
const COMPLETE_ATTESTATION_URL: RegExp = /\/cashRegisterModels\/(\d+)\/endAttestation$/;
const ELIMINATE_MODEL_URL: RegExp = /\/cashRegisterModels\/(\d+)\/eliminate$/;
const ACTIVATE_MODEL_URL: RegExp = /\/cashRegisterModels\/(\d+)\/activate$/;
const MARK_VERSION_OUTDATED_URL: RegExp = /\/cashRegisterModels\/(\d+)\/versions\/(\d+)\/outdated$/;
const ACTIVATE_VERSION_URL: RegExp = /\/cashRegisterModels\/(\d+)\/versions\/(\d+)\/activate$/;
const MODEL_DOCUMENTS_URL: RegExp = /\/cashRegisterModels\/(\d+)\/documents$/;
const SUBMIT_MODEL_DOCUMENTS_URL: RegExp = /\/cashRegisterModels\/(\d+)\/documents\/submit$/;
const DOCUMENT_FOR_DOWNLOAD_URL: RegExp = /\/cashRegisterModels\/(\d+)\/documents\/(\d+)\/url$/;
const VERSION_NAMES_URL: RegExp = /\/cashRegisterModels\/(\d+)\/versionNames$/;

class CashRegisterModelsFeatureMock extends BaseFeatureMock {
    public readonly list = this.route<CashRegisterModelsListDto>({
        method: 'get',
        url: CASH_REGISTER_MODELS_URL,
        defaultResponse: (config) => {
            const data: CashRegisterModelsListDto = {
                count: config.params.limit * 3,
                cashRegisterModels: cashRegisterModelsListItemFactory.list({
                    count: config.params.limit,
                }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly details = this.route<CashRegisterModelDetailsDto>({
        method: 'get',
        url: CASH_REGISTER_MODEL_URL,
        defaultResponse: () => {
            const data: CashRegisterModelDetailsDto = cashRegisterModelDetailsDtoFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly versions = this.route<VersionsListDto>({
        method: 'get',
        url: MODEL_VERSIONS_URL,
        defaultResponse: (config) => {
            let data: VersionsListDto;
            if (config.params.limit) {
                data = {
                    count: config.params.limit * 3,
                    versions: versionDtoFactory.list({ count: config.params.limit }),
                };
            } else {
                data = {
                    count: 45,
                    versions: versionDtoFactory.list({ count: 45 }),
                };
            }

            return [200, data];
        },
        delayMs: () => Math.random() * 3000,
    });

    public readonly versionCheckSums = this.route<CheckSumsListDto>({
        method: 'get',
        url: VERSION_CHECK_SUMS_URL,
        defaultResponse: (config) => {
            const data: CheckSumsListDto = {
                count: config.params.limit * 100,
                checksums: checkSumDtoFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 300,
    });

    public readonly factoryNumbers = this.route<FactoryNumbersListDto>({
        method: 'get',
        url: CASH_REGISTERS_FACTORY_NUMBERS_URL,
        defaultResponse: (config) => {
            const data: FactoryNumbersListDto = {
                count: 35,
                cashRegisters: cashRegistersFactoryNumbersListDto.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly update = this.route<{}>({
        method: 'patch',
        url: CASH_REGISTER_MODEL_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 100,
    });

    public readonly startAttestation = this.route<{}>({
        method: 'post',
        url: START_ATTESTATION_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 100,
    });

    public readonly completeAttestation = this.route<AttestationResponseDto>({
        method: 'post',
        url: COMPLETE_ATTESTATION_URL,
        defaultResponse: () => {
            const data: AttestationResponseDto = {
                accepted: false,
                responseStatus: AttestationResponseStatus.FW_VERSION_OUTDATED,
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly eliminateModel = this.route<{}>({
        method: 'post',
        url: ELIMINATE_MODEL_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 1000,
    });

    public readonly activateModel = this.route<{}>({
        method: 'post',
        url: ACTIVATE_MODEL_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 1000,
    });

    public readonly markVersionOutdated = this.route<{}>({
        method: 'post',
        url: MARK_VERSION_OUTDATED_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 1000,
    });

    public readonly activateVersion = this.route<{}>({
        method: 'post',
        url: ACTIVATE_VERSION_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 1000,
    });

    public readonly documents = this.route<ModelDocumentsListDto>({
        method: 'get',
        url: MODEL_DOCUMENTS_URL,
        defaultResponse: (config) => {
            const data: ModelDocumentsListDto = {
                count: 30,
                files: modelDocumentDtoFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly uploadDocument = this.route<PresignedFileInfo>({
        method: 'post',
        url: MODEL_DOCUMENTS_URL,
        defaultResponse: () => {
            const data: PresignedFileInfo = presignedFileInfoDtoFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly deleteDocuments = this.route<{}>({
        method: 'delete',
        url: MODEL_DOCUMENTS_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 1000,
    });

    public readonly submitDocuments = this.route<{}>({
        method: 'post',
        url: SUBMIT_MODEL_DOCUMENTS_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 1000,
    });

    public readonly documentUrl = this.route<string>({
        method: 'get',
        url: DOCUMENT_FOR_DOWNLOAD_URL,
        defaultResponse: () => [200, 'http://someUrl'],
        delayMs: () => Math.random() * 1000,
    });

    public readonly versionNames = this.route<SoftwareVersionItemDto[]>({
        method: 'get',
        url: VERSION_NAMES_URL,
        defaultResponse: () => {
            const data = softwareVersionDataFactory.list({ count: 7 });
            softwareVersionDataFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 3000,
    });

    protected onReset(): void {
        cashRegisterModelsListItemFactory.resetCount();
        cashRegisterModelDetailsDtoFactory.resetCount();
        cashRegistersFactoryNumbersListDto.resetCount();
        versionDtoFactory.resetCount();
        checkSumDtoFactory.resetCount();
        modelDocumentDtoFactory.resetCount();
        presignedFileInfoDtoFactory.resetCount();
        softwareVersionDataFactory.resetCount();
    }
}

export const cashRegisterModelsMock = new CashRegisterModelsFeatureMock();

/** Backward-compat shim - dev mock mode (mockAdapter.ts) keeps calling this untouched. */
export function addCashRegisterModelsMockApi(mockAdapter: MockAdapter): void {
    cashRegisterModelsMock.register(mockAdapter, { delay: true });
}
